import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  TextField,
  InputBase,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemButton,
  Popover,
  Paper,
} from '@mui/material';

export const SearchContainer = styled('div')(({ theme }) => ({
  maxWidth: '1496px',
  margin: '0 auto',
  padding: '22px 36px',
  backgroundColor: '#FEF1EA',
  borderRadius: '10px',

  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  
  // Tablet styles - 3 rows layout with different column structures
  [theme.breakpoints.between('sm', 'lg')]: {
    maxWidth: '720px',
    padding: '24px 32px 40px',
    display: 'grid',
    gridTemplateColumns: 'repeat(12, 1fr)', // 12 column grid for flexibility
    gridTemplateRows: 'auto auto auto',
    gap: '8px',
    height: 'auto',
    
    // First row - Transaction and Property dropdowns (50% 50%)
    '& > div:nth-of-type(1)': {
      gridColumn: '1 / 7', // columns 1-6 (50%)
      gridRow: '1',
    },
    '& > div:nth-of-type(2)': {
      gridColumn: '7 / 13', // columns 7-12 (50%)
      gridRow: '1',
    },
    
    // Second row - Search input (full width)
    '& > div:nth-of-type(3)': {
      gridColumn: '1 / 13', // full width
      gridRow: '2',
    },
    
    // Third row - Filter dropdown and Search button (80% | 20%)
    '& > div:nth-of-type(4)': {  // FilterDropdown
      gridColumn: '1 / 10', // columns 1-9 (80%)
      gridRow: '3',
    },
    '& > button': {  // SearchButton
      gridColumn: '10 / 13', // columns 10-12 (20%)
      gridRow: '3',
    },
  },
  
  // Mobile styles
  [theme.breakpoints.down('sm')]: {
    display: 'none', // Hide on mobile - mobile version should be separate component
  },
}));

export const SearchInput = styled(InputBase)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '25px',
  border: '1px solid #D3D3D3',
  padding: theme.spacing(0.5, 2),
  flex: 1,
  height: '40px',
  width: '100%',
  '&::placeholder': {
    color: '#919192',
    opacity: 1,
  },
  '&:hover, &.Mui-focused': {
    border: '1px solid #F4762A',
  },
  // Tablet styles
  [theme.breakpoints.between('sm', 'lg')]: {
    height: '44px',
    borderRadius: '40px',
    fontSize: '16px',
    fontFamily: 'Prompt, sans-serif',
  },
}));

export const SearchButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#F4762A',
  color: '#ffffff',
  borderRadius: '25px',
  height: '40px',
  padding: theme.spacing(0, 3),
  minWidth: '100px',
  fontWeight: 500,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#E55A1F',
  },
  // Tablet styles
  [theme.breakpoints.between('sm', 'lg')]: {
    height: '44px',
    borderRadius: '40px',
    fontSize: '16px',
    fontFamily: 'Prompt, sans-serif',
  },
}));

export const SearchDropdownContainer = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 1000,
  borderRadius: '8px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
  marginTop: '4px',
  maxHeight: '400px',
  overflow: 'hidden',
  backgroundColor: 'white',
}));

export const SearchDropdownItem = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  cursor: 'pointer',
  borderBottom: '1px solid #f0f0f0',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '&:last-child': {
    borderBottom: 'none',
  },
}));

export const FilterPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '20px',
    boxShadow: 'none',
    width: '100%',
    maxWidth: '752px',
    margin: '8px 0',
    overflow: 'hidden',
    padding: '0px 12px',
  },
  '& ::-webkit-scrollbar': {
    width: '1px',
    borderRadius: '10px',
  },
  '& ::-webkit-scrollbar-track': {
    background: 'transparent !important',
  },
  '& ::-webkit-scrollbar-thumb': {
    background: '#575758',
    borderRadius: '10px',
  },
  '& ::-webkit-scrollbar-thumb:hover': {
    background: '#F4762A',
  },
  '& *': {
    scrollbarWidth: 'thin',
    scrollbarColor: '#575758 transparent',
  },
}));

export const FilterAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  backgroundColor: 'transparent',
  transition: 'all 0.3s ease',
  '&.Mui-expanded': {
    margin: '4px 0',
    borderRadius: '8px',
    backgroundColor: '#FEF1EA10',
  },
}));

export const FilterAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: '12px 16px',
  '& .MuiAccordionSummary-content': {
    margin: 0,
  },
  '&.Mui-expanded': {
    minHeight: '48px',
    backgroundColor: '#FEF1EA',
    borderRadius: '8px',
  },
  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  '& .MuiSvgIcon-root': {
    transition: 'transform 0.3s ease-in-out',
    color: '#666666',
  },
  '&.Mui-expanded .MuiSvgIcon-root': {
    transform: 'rotate(180deg)',
    color: '#F4762A',
  },
  '&:focus-within': {
    backgroundColor: '#FEF1EA80',
    borderRadius: '8px',
  },
  '& .MuiTypography-root': {
    fontWeight: 500,
    transition: 'color 0.2s ease',
    color: '#919192',
  },
  '&.Mui-expanded .MuiTypography-root': {
    color: '#F4762A',
    fontWeight: 600,
  },
}));

export const FilterAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: '16px 16px',
  maxHeight: '270px',
  overflowY: 'auto',
  marginTop: '8px',
  background: '#fff',
  borderRadius: '8px',
  '& ::-webkit-scrollbar': {
    width: '1px',
    borderRadius: '10px',
  },
  '& ::-webkit-scrollbar-track': {
    background: 'transparent !important',
  },
  '& ::-webkit-scrollbar-thumb': {
    background: '#575758',
    borderRadius: '10px',
  },
  '& ::-webkit-scrollbar-thumb:hover': {
    background: '#F4762A',
  },
  '& *': {
    scrollbarWidth: 'thin',
    scrollbarColor: '#575758 transparent',
  },
}));

export const FilterButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#ffffff',
  color: '#333333',
  border: '1px solid #D3D3D3',
  borderRadius: '25px',
  height: '40px',
  padding: theme.spacing(0, 2),
  minWidth: '120px',
  fontWeight: 400,
  textTransform: 'none',
  gap: '8px',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    border: '1px solid #F4762A',
  },
  // Tablet styles
  [theme.breakpoints.between('sm', 'lg')]: {
    height: '44px',
    borderRadius: '40px',
    fontSize: '16px',
    fontFamily: 'Prompt, sans-serif',
    width: '100%',
  },
}));

export const PriceButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#ffffff',
  color: '#333333',
  border: '1px solid #D3D3D3',
  borderRadius: '8px',
  padding: '8px 12px',
  minWidth: '60px',
  fontSize: '14px',
  fontWeight: 400,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    border: '1px solid #F4762A',
  },
  '&.selected': {
    backgroundColor: '#F4762A',
    color: '#ffffff',
    border: '1px solid #F4762A',
    '&:hover': {
      backgroundColor: '#E55A1F',
    },
  },
}));

export const PriceRangeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '16px',
}));

export const PriceRangeInput = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    height: '36px',
    borderRadius: '8px',
    fontSize: '14px',
    '& fieldset': {
      borderColor: '#D3D3D3',
    },
    '&:hover fieldset': {
      borderColor: '#F4762A',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#F4762A',
    },
  },
  '& .MuiInputBase-input': {
    padding: '8px 12px',
  },
}));

export const PriceRangeSeparator = styled(Box)(({ theme }) => ({
  padding: '0 4px',
  color: '#666666',
  fontSize: '14px',
}));

export const ActionButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '12px',
  marginTop: '24px',
  padding: '16px 0 8px',
}));



export const ActionButtonPrimary = styled(Button)(({ theme }) => ({
  flex: 1,
  height: '40px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  
  '&.MuiButton-outlined': {
    backgroundColor: '#ffffff',
    color: '#333333',
    border: '1px solid #D3D3D3',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      border: '1px solid #F4762A',
    },
  },
  
  '&.MuiButton-contained': {
    backgroundColor: '#F4762A',
    color: '#ffffff',
    border: '1px solid #F4762A',
    '&:hover': {
      backgroundColor: '#E55A1F',
    },
  },
}));

export const FilterOptionGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
  gap: '8px',
  marginBottom: '16px',
}));

export const FilterOptionButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#ffffff',
  color: '#333333',
  border: '1px solid #D3D3D3',
  borderRadius: '8px',
  padding: '8px 12px',
  minWidth: '60px',
  fontSize: '14px',
  fontWeight: 400,
  textTransform: 'none',
  
  '&:hover': {
    backgroundColor: '#f5f5f5',
    border: '1px solid #F4762A',
  },
  
  '&.MuiButton-contained': {
    backgroundColor: '#F4762A',
    color: '#ffffff',
    border: '1px solid #F4762A',
    '&:hover': {
      backgroundColor: '#E55A1F',
    },
  },
  
  '&.selected': {
    color: '#F4762A',
    border: '1px solid #F4762A',
    '&:hover': {
      backgroundColor: '#E55A1F',
    },
  },
}));

export const DropdownContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  minWidth: '150px',
}));

export const DropdownButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#ffffff',
  color: '#333333',
  border: '1px solid #D3D3D3',
  borderRadius: '25px',
  height: '40px',
  padding: theme.spacing(0, 2),
  width: '100%',
  justifyContent: 'space-between',
  fontWeight: 400,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    border: '1px solid #F4762A',
  },
  // Tablet styles
  [theme.breakpoints.between('sm', 'lg')]: {
    height: '44px',
    borderRadius: '40px',
    fontSize: '16px',
    fontFamily: 'Prompt, sans-serif',
  },
}));

export const DropdownMenu = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 1000,
  borderRadius: '8px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
  marginTop: '4px',
  maxHeight: '200px',
  overflow: 'auto',
  backgroundColor: 'white',
}));

export const DropdownItem = styled(ListItemButton)(({ theme }) => ({
  padding: '12px 16px',
  borderBottom: '1px solid #f0f0f0',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '&:last-child': {
    borderBottom: 'none',
  },
  '&.selected': {
    backgroundColor: '#FEF1EA',
    color: '#F4762A',
  },
}));

export const StyledInputBase = styled(InputBase)(({ theme }) => ({
  width: '332px',
  height: '43px',
  borderRadius: '40px',
  gap: '8px',
  padding: '12px 24px',
  border: '1px solid #D3D3D3',
  background: '#FFFFFF',
  fontFamily: 'Prompt, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  '&:hover, &.Mui-focused': {
    border: '1px solid #F4762A',
  },
}));

export const LocationPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '20px',
    boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)',
    width: '100%',
    maxWidth: '752px',
    margin: '8px 0',
    overflow: 'hidden',
    padding: '0px 12px',
  },
  '&.searchMode .MuiPaper-root': {
    width: 'auto',
    minWidth: '300px',
  },
  '& ::-webkit-scrollbar': {
    width: '1px',
    borderRadius: '10px',
  },
  '& ::-webkit-scrollbar-track': {
    background: 'transparent !important',
  },
  '& ::-webkit-scrollbar-thumb': {
    background: '#575758',
    borderRadius: '10px',
  },
  '& ::-webkit-scrollbar-thumb:hover': {
    background: '#F4762A',
  },
  '& *': {
    scrollbarWidth: 'thin',
    scrollbarColor: '#575758 transparent',
  },
}));

export const LocationButton = styled(Button)(({ theme }) => ({
  width: '100%',
  justifyContent: 'space-between',
  padding: '10px 16px',
  textAlign: 'left',
  textTransform: 'none',
  borderRadius: '0',
  color: '#000',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  borderBottom: '1px solid #EEEEEE',
}));

export const LocationListItem = styled(ListItemButton)(({ theme }) => ({
  padding: '12px 16px',
  borderRadius: '0px',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  borderBottom: '1px solid #EEEEEE',
  margin: '0',
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

export const ScrollableBox = styled(Box)(({ theme }) => ({
  maxHeight: '450px',
  overflowY: 'auto',
  padding: '10px',
  width: '100%',
  '&::-webkit-scrollbar': {
    width: '1px',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent !important',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#575758',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#F4762A',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: '#575758 transparent',
}));

export const ModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '635px',
  maxHeight: '600px',
  bgcolor: 'background.paper',
  borderRadius: '10px',
  boxShadow: 24,
  p: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

export const ModalHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#F4762A',
  padding: '16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: 'white',
  minHeight: '64px',
}));

export const ModalSearchInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    height: '42px',
    borderRadius: '25px',
    backgroundColor: 'white',
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#F4762A',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#F4762A',
    },
    '& .MuiInputAdornment-root': {
      marginLeft: '8px',
    },
  },
}));

export const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '25px',
  padding: theme.spacing(1, 3),
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
})); 